import { action } from '@ember/object';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismDrawer,
  PrismLoadingBar,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import SettingsBudgetForecastVersionHistoryDrawerGroup from 'lumen/components/settings/budget-forecast/version-history-drawer/group';
import isDrawerExpanded from 'lumen/helpers/is-drawer-expanded';
import type { BudgetForecastVersion } from 'lumen/models';
import {
  DRAWER_IDS,
  FIRST_HEADER_HEIGHT,
} from 'lumen/utils/constants/general-constants';

import styles from './version-history-drawer.module.css';

function groupVersionsByDate(
  versions: BudgetForecastVersion[],
): Record<string, BudgetForecastVersion[]> {
  return versions.reduce<Record<string, BudgetForecastVersion[]>>(
    (acc, version) => {
      const dateKey = version.date.local().format('YYYY-MM-DD');

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }
      acc[dateKey].push(version);

      return acc;
    },
    {},
  );
}

interface SettingsBudgetForecastVersionHistoryDrawerSignature {
  Args: {
    isLoading: boolean;
    onSelectVersion: (versionId: string | undefined) => void;
    selectedVersionId: string | undefined;
    versions: BudgetForecastVersion[];
  };
}

export default class SettingsBudgetForecastVersionHistoryDrawer extends Component<SettingsBudgetForecastVersionHistoryDrawerSignature> {
  @service declare drawer: Services['drawer'];

  get latestVersionId(): string | undefined {
    if (!this.args.versions[0]) {
      return undefined;
    }

    const latestVersion = this.args.versions.reduce((latest, current) => {
      return current.date.unix() > latest.date.unix() ? current : latest;
    }, this.args.versions[0]);

    return latestVersion.id!;
  }

  /**
   * If no version is selected, we want to highlight the most recent version.
   */
  get selectedVersionIdWithDefault(): string | undefined {
    if (this.args.selectedVersionId) {
      return this.args.selectedVersionId;
    }

    return this.latestVersionId;
  }

  get versionGroups(): {
    date: Dayjs;
    id: string;
    versions: BudgetForecastVersion[];
  }[] {
    const sortedEntries = this.args.versions.toSorted(
      (a, b) => b.date.unix() - a.date.unix(),
    );

    return Object.entries(groupVersionsByDate(sortedEntries)).map(
      ([date, versions]) => ({
        id: date,
        date: dayjs(date),
        versions: versions,
      }),
    );
  }

  @action
  collapseDrawer(): void {
    this.drawer.collapseDrawer(DRAWER_IDS.BUDGET_FORECAST_VERSION_HISTORY);
  }

  @action
  onSelectVersion(versionId: string): void {
    const isLatestVersionClicked = versionId === this.latestVersionId;
    if (isLatestVersionClicked) {
      this.args.onSelectVersion(undefined);
      return;
    }

    this.args.onSelectVersion(versionId);
  }

  <template>
    <PrismDrawer
      @contentPadding="none"
      @isOpen={{isDrawerExpanded DRAWER_IDS.BUDGET_FORECAST_VERSION_HISTORY}}
      @positionTop={{FIRST_HEADER_HEIGHT}}
      class={{styles.container}}
      data-test-version-history-drawer
    >
      <:content>
        <div class="flex h-full flex-col">
          <div
            class="border-neutral-default flex items-center justify-between gap-200 border-b py-200 pr-200 pl-400"
            data-test-header
          >
            <h4>{{t "budget_forecast.version_history"}}</h4>

            <PrismButton
              @buttonType="ghost"
              @icon="cross"
              @onClick={{this.collapseDrawer}}
              data-test-button="close-drawer"
            />
          </div>

          {{#if @isLoading}}
            <div class="relative">
              <PrismLoadingBar data-test-loading-bar />
            </div>
          {{else if @versions.length}}
            <div class="grid gap-400 overflow-y-auto p-400" data-test-content>
              {{#each this.versionGroups key="id" as |group|}}
                <SettingsBudgetForecastVersionHistoryDrawerGroup
                  @date={{group.date}}
                  @onSelectVersion={{this.onSelectVersion}}
                  @selectedVersionId={{this.selectedVersionIdWithDefault}}
                  @versions={{group.versions}}
                />
              {{/each}}
            </div>
          {{else}}
            <div
              class="flex flex-1 items-center justify-center"
              data-test-empty-state
            >
              <PrismMessage
                @description={{t
                  "budget_forecast.version_history.empty_state.description"
                }}
                @illustration="100x100_binoculars"
                @title={{t "budget_forecast.version_history.empty_state.title"}}
              />
            </div>
          {{/if}}
        </div>
      </:content>
    </PrismDrawer>
  </template>
}
